import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Gift } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = '"Cormorant Garamond", Georgia, serif';
const C = { ink: "#0a132a", cream: "#F1ECDD", dim: "rgba(241,236,221,0.66)", gold: "#D7BB7A" };

// Public landing for a coach's private link: personal, one button, coach attribution kept through sign-up.
export default function CoachGiftLanding() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [w, setW] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/soulumo/coach-prep/${encodeURIComponent(id)}/welcome`).then((r) => {
      setW(r.data);
      if (r.data.ref && !localStorage.getItem("reclaim_referral_code")) localStorage.setItem("reclaim_referral_code", r.data.ref);
    }).catch(() => setMissing(true));
  }, [id]);
  const next = `/my-journeys?prep=${encodeURIComponent(id)}`;
  // a real listen = 30s actually played, counted once per device
  const played = React.useRef({ last: null, total: 0, sent: false });
  const countListen = (a) => {
    const p = played.current;
    if (p.sent) return;
    if (p.last !== null && a.currentTime > p.last && a.currentTime - p.last < 2) p.total += a.currentTime - p.last;
    p.last = a.currentTime;
    if (p.total < 30) return;
    p.sent = true;
    let key = localStorage.getItem("soulumo_listener");
    if (!key) { key = `l_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("soulumo_listener", key); }
    axios.post(`${API}/api/soulumo/coach-prep/${encodeURIComponent(id)}/listened`, { listener: key }).catch(() => {});
  };
  const begin = () => (user || localStorage.getItem("reclaim_token") ? nav(next) : nav(`/soulumo/join?next=${encodeURIComponent(next)}`));

  return (
    <div className="min-h-screen flex items-center px-6" style={{ background: `radial-gradient(ellipse at 50% 0%, #1b2f27 0%, ${C.ink} 65%)`, color: C.cream }} data-testid="coach-gift-landing">
      <div className="max-w-lg mx-auto text-center">
        <p className="text-[0.65rem] uppercase tracking-[0.4em]" style={{ color: C.gold }}>Soulumo</p>
        {w && (w.coach_photo
          ? <img src={`${API}${w.coach_photo}`} alt={w.coach_name} className="mx-auto mt-8 w-24 h-24 rounded-full object-cover" style={{ border: `1px solid ${C.gold}` }} data-testid="coach-gift-photo" />
          : <span className="mx-auto mt-8 w-24 h-24 rounded-full flex items-center justify-center text-4xl" style={{ background: "#17322A", color: C.gold, fontFamily: SERIF, border: `1px solid ${C.gold}` }} data-testid="coach-gift-initials">{w.coach_initials}</span>)}
        {missing ? (
          <>
          <p className="mt-8 text-2xl italic" style={{ fontFamily: SERIF, color: C.cream }} data-testid="coach-gift-missing">This link isn't ready yet. Ask your coach to send it again.</p>
            <a href="/soulumo" className="mt-8 inline-block text-xs uppercase tracking-[0.3em] underline" style={{ color: C.gold }} data-testid="coach-gift-home-link">Go to Soulumo</a>
          </>
        ) : !w ? null : (
          <>
            <h1 className="mt-8 text-4xl sm:text-5xl leading-tight" style={{ fontFamily: SERIF, color: C.cream }} data-testid="coach-gift-title">
              {w.coach_name} has created a Soulumo for you{w.client_name ? `, ${w.client_name}` : ""}.
            </h1>
            <p className="mt-5 text-base md:text-lg" style={{ color: C.dim }}>Your personalised affirmations are ready. Now make them yours.</p>
            {w.note && <p className="mt-6 text-xl italic" style={{ fontFamily: SERIF, color: C.cream }} data-testid="coach-gift-note">“{w.note}”</p>}
            {w.listen && (
              <div className="mt-8 rounded-2xl p-4 text-left" style={{ background: "rgba(241,236,221,0.05)", border: "1px solid rgba(215,187,122,0.25)" }} data-testid="coach-gift-listen">
                <p className="text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: C.gold }}>Listen now · in {w.coach_name}'s voice</p>
                {w.listen.audio_url
                  ? <audio controls preload="none" src={`${API}${w.listen.audio_url}`} className="mt-3 w-full" data-testid="coach-gift-audio" onTimeUpdate={(e) => countListen(e.target)} />
                  : <p className="mt-2 text-sm" style={{ color: C.dim }}>{w.coach_name}'s recording is being made. Check back in a few minutes.</p>}
              </div>
            )}
            {w.gift && (
              <p className="mt-6 inline-flex items-center gap-2 text-sm" style={{ color: C.gold }} data-testid="coach-gift-covered">
                <Gift size={15} /> A gift from {w.coach_name}: your first personal installation is covered.
              </p>
            )}
            <button type="button" onClick={begin} data-testid="coach-gift-begin"
              className="mt-10 w-full rounded-full py-4 text-xs uppercase tracking-[0.18em] font-bold transition-transform duration-200 active:scale-[0.98]"
              style={{ background: C.gold, color: C.ink }}>{w.listen ? "Make it yours, in your own voice" : "Begin"}</button>
            {!user && (
              <button type="button" onClick={() => nav(`/soulumo/join?mode=signin&next=${encodeURIComponent(next)}`)} className="mt-4 text-sm underline" style={{ color: C.dim }} data-testid="coach-gift-signin">
                Already have Soulumo? Sign in
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
